/**
 * Reusable canvas signature pad. Returns dataURL on save.
 *
 *   <SignaturePad onChange={(dataUrl) => setSig(dataUrl)} />
 */
import { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

export default function SignaturePad({ onChange, height = 160 }) {
  const ref = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#C9A055";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pos = (e) => {
    const c = ref.current;
    const r = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    return { x: x * (c.width / r.width), y: y * (c.height / r.height) };
  };

  const start = (e) => { e.preventDefault(); const { x, y } = pos(e); const ctx = ref.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); setDrawing(true); };
  const move = (e) => { if (!drawing) return; e.preventDefault(); const { x, y } = pos(e); const ctx = ref.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); setEmpty(false); };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    if (!empty) onChange?.(ref.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = ref.current; const ctx = c.getContext("2d");
    ctx.fillStyle = "#0a0a0a"; ctx.fillRect(0, 0, c.width, c.height);
    setEmpty(true); onChange?.("");
  };

  return (
    <div>
      <canvas
        ref={ref} width={600} height={height}
        data-testid="signature-pad"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full border border-white/15 cursor-crosshair touch-none"
        style={{ background: "#0a0a0a", maxHeight: height }}
      />
      <div className="flex justify-between items-center mt-1">
        <span className="font-ui text-[10px] tracking-[0.18em] uppercase text-[#8a857d]">
          {empty ? "Draw your signature above" : "Looks good — re-draw if needed"}
        </span>
        <button type="button" onClick={clear} data-testid="signature-clear"
          className="text-[#8a857d] hover:text-[#C9A055] text-[11px] inline-flex items-center gap-1">
          <Eraser size={12} /> Clear
        </button>
      </div>
    </div>
  );
}
